import { Drawer, IconNode } from '@components/base';
import css from './index.module.scss';
import Images from '@assets/images';
import { FormProvider, useForm } from 'react-hook-form';
import {
  DEFAULT_CURRENCY,
  GENERIC_INFORMATION_DEFAULT_VALUE,
  GENERIC_INFORMATION_DOMESTIC_DEFAULT_VALUE
} from '@components/common/quote-wrapper/components/helper';
import { useEffect, useState } from 'react';
import { fetchEntityList, getPaymentTermsFilterOptions } from '@services/order.service';
import notify from '@helpers/toastify-helper';
import { CURRENCY, ENQUIRY_TYPE, TAX_OPTIONS, WEIGHT_SYSTEM } from '@helpers/constants';
import { editEnquiryAndProduct, fetchQuoteCustomerList } from '@services/quote.service';
import EnquiryGenericInformation from '@components/common/quote-wrapper/components/generic-information';
import { IWeightSystemOption } from '@helpers/types/quotes';
import EnquiryForm from '@components/common/quote-wrapper/components/enquiry-form';
import { getApiErrorMessage } from '@components/common/quote-wrapper/components/helper';
import moment from 'moment';
import { useParams } from 'react-router-dom';

interface IEnquiryEditProps {
  open: boolean;
  onClose: () => void;
  onSubmitClose?: () => void;
  data: any;
  enquiryType?: string;
}

const EnquiryEdit = (props: IEnquiryEditProps) => {
  const { open, onClose, onSubmitClose, data, enquiryType = ENQUIRY_TYPE.INTERNATIONAL } = props;
  const params = useParams();

  const [enquiryEditState, setEnquiryEditState] = useState<any>({
    isLoading: false,
    paymentTermsOptions: [],
    entityOptions: [],
    customerOptions: [],
    filteredCustomerOptions: [],
    selectedCurrency: [DEFAULT_CURRENCY],
    selectedWeightSystemChildOptions: WEIGHT_SYSTEM.reduce(
      (acc: any, system: IWeightSystemOption) => {
        if (system.childOptions) {
          acc = acc.concat(system.childOptions);
        }
        return acc;
      },
      []
    ),
    enquiryId: params?.id,
    isDomestic: enquiryType === ENQUIRY_TYPE.DOMESTIC
  });

  const {
    isLoading,
    paymentTermsOptions,
    entityOptions,
    customerOptions,
    portOptions,
    selectedWeightSystemChildOptions,
    enquiryId,
    isDomestic
  } = enquiryEditState;

  const getPaymentTermsOptions = async () => {
    setEnquiryEditState((prevState: any) => ({ ...prevState, isLoading: true }));
    const response = await getPaymentTermsFilterOptions();
    if (response?.success) {
      setEnquiryEditState((prevState: any) => ({
        ...prevState,
        paymentTermsOptions: response.data?.payment_terms_options,
        isLoading: false
      }));
    } else {
      notify({
        severity: 'error',
        title: 'Error',
        message: response.message ?? 'Unable to fetch payment terms'
      });
      setEnquiryEditState((prevState: any) => ({ ...prevState, isLoading: false }));
    }
  };

  const getEntityList = async () => {
    setEnquiryEditState((prevState: any) => ({ ...prevState, isLoading: true }));
    const response = await fetchEntityList();
    if (response?.success) {
      setEnquiryEditState((prevState: any) => ({
        ...prevState,
        entityOptions: response?.data?.results ?? [],
        isLoading: false
      }));
    } else {
      setEnquiryEditState((prevState: any) => ({ ...prevState, isLoading: false }));
      notify({
        severity: 'error',
        message: response?.message
      });
    }
  };

  const filterCustomerOptions = async (event: any, param?: any) => {
    if (param) {
      const response = await fetchQuoteCustomerList({ search: param });
      if (response?.success) {
        setEnquiryEditState((prevState: any) => ({
          ...prevState,
          customerOptions: response?.data ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    } else {
      const response = await fetchQuoteCustomerList();
      if (response?.success) {
        setEnquiryEditState((prevState: any) => ({
          ...prevState,
          customerOptions: response?.data ?? []
        }));
      } else {
        notify({
          severity: 'error',
          message: response?.message
        });
      }
    }
  };

  const form = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    //   resolver: yupResolver(),
    defaultValues: {
      genericInformation: isDomestic
        ? GENERIC_INFORMATION_DOMESTIC_DEFAULT_VALUE
        : GENERIC_INFORMATION_DEFAULT_VALUE,
      enquiryNumber: null,
      customer: null,
      entity: null,
      payment_terms: [
        {
          payment_term: null,
          percentage: null,
          days: null
        }
      ],
      priceValidUntill: null,
      expectedDeliverySchedule: null
    }
  });

  const onFormSubmit = async (data: any) => {
    if (!data?.priceValidUntill) {
      form.setError('priceValidUntill', {
        type: 'required',
        message: 'Price Validity Until is required'
      });
      return;
    }
    let totalPercentage = 0,
      isError = false;
    data?.payment_terms?.forEach((item: any) => {
      totalPercentage += parseInt(item.percentage);
      if (item?.payment_term?.days && (!item?.days || parseInt(item?.days) <= 0)) {
        notify({
          message: `Enter valid number of days for ${item?.payment_term?.payment_term}`,
          severity: 'error'
        });
        isError = true;
        return;
      }
    });
    if (totalPercentage !== 100) {
      notify({
        message: 'Total percentage in payment terms should be 100',
        severity: 'error'
      });
      isError = true;
      return;
    }
    if (isError) return;
    const reqBody = {
      currency_type_one: data.genericInformation.currency1.value ?? null,
      currency_type_two: data.genericInformation.currency2?.value ?? null,
      fx_rate: data.genericInformation.fxRate,
      weight_system: data.genericInformation.weightSystem.value,
      deal_name: data.genericInformation?.draftDealName,
      remarks: data?.remarks,
      primary_unit: data.genericInformation.primaryUnit.value, // Access the value property here
      entity: data.entity.entity_id,
      last_valid_date: data.priceValidUntill
        ? `${moment(data.priceValidUntill).format('YYYY-MM-DD')}`
        : null,
      expected_delivery_schedule: data.expectedDeliverySchedule,
      payment_term: (data.payment_terms || []).map((term: any) => {
        const obj: any = {};
        obj['payment_term'] = term.payment_term?.payment_term;
        obj['percentage'] = parseInt(term.percentage);
        if (term.days) {
          obj['days'] = parseInt(term.days);
        }
        return obj;
      })
    };

    const response = await editEnquiryAndProduct(enquiryId, { enquiry_details: reqBody });
    if (response?.success) {
      const { data } = response;
      notify({
        severity: 'success',
        message: data.message
      });
      onSubmitClose && onSubmitClose();
    } else {
      notify({
        severity: 'error',
        message: getApiErrorMessage(response)
      });
    }
  };

  useEffect(() => {
    getPaymentTermsOptions();
    getEntityList();
    filterCustomerOptions(null, '');
  }, []);

  useEffect(() => {
    let genericInformation = {};
    if (isDomestic) {
      genericInformation = {
        currency1: CURRENCY.find((item) => data?.currency_type_one === item.value),
        weightSystem: WEIGHT_SYSTEM.find((item) => item.value === data?.weight_system),
        primaryUnit: WEIGHT_SYSTEM.find(
          (item) => item.value === data?.weight_system
        )?.childOptions.find((child) => child.value === data?.primary_unit),
        draftDealName: data?.deal_name,
        taxType: TAX_OPTIONS.find((item) => item.value === data?.tax_type) ?? null
      };
    } else {
      genericInformation = {
        currency1: CURRENCY.find((item) => data?.currency_type_one === item.value) ?? null,
        currency2: CURRENCY.find((item) => data?.currency_type_two === item.value) ?? null,
        fxRate: data?.fx_rate,
        weightSystem: WEIGHT_SYSTEM.find((item) => item.value === data?.weight_system) ?? null,
        primaryUnit: WEIGHT_SYSTEM.find(
          (item) => item.value === data?.weight_system
        )?.childOptions.find((child) => child.value === data?.primary_unit),
        draftDealName: data?.deal_name
      };
    }
    form.reset({
      genericInformation: genericInformation,
      enquiryNumber: {
        value: data.enquiry_id,
        label: data.readable_enquiry_number
      },
      customer: {
        id: data.hubspot_customer_id,
        customer_name: data.customer_name
      },
      entity: {
        entity_id: data.entity?.entity_id,
        name: data.entity?.name
      },
      remarks: data.remarks || '',
      Createddate: new Date(data.created_on),
      priceValidUntill: data?.last_valid_date && new Date(data.last_valid_date),
      payment_terms: (data.payment_terms || []).map((term: any) => {
        return {
          payment_term: {
            payment_term: term.payment_term,
            days: term.days ? true : false,
            percentage: term.percentage ? true : false
          },
          percentage: term.percentage,
          days: term.days
        };
      }),
      expectedDeliverySchedule: data.expected_delivery_schedule
    });
  }, [data]);

  return (
    <Drawer open={open} anchor="right" onClose={onClose}>
      <div className={css.mainWrapper}>
        <div className={css.header}>
          <div className={css.quoteHeading}>Edit Enquiry</div>
          <IconNode
            src={Images.close}
            alt="quote icon"
            onClick={() => onClose()}
            className={css.closeIcon}
          />
        </div>
        <FormProvider {...form}>
          <EnquiryGenericInformation
            setQuoteFormState={setEnquiryEditState}
            selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
            enquiryType={enquiryType}
          />
          <EnquiryForm
            paymentTermsOptions={paymentTermsOptions}
            entityOptions={entityOptions}
            onFormSubmit={onFormSubmit}
            customerOptions={customerOptions}
            filterCustomerOptions={filterCustomerOptions}
            isEdit={true}
          />
        </FormProvider>
        <div className={css.contentWrapper}></div>
      </div>
    </Drawer>
  );
};

export default EnquiryEdit;
